import {
  locationCalendarPath,
  locationCustomersPath,
  locationDashboardPath,
  locationDetailingProductsPath,
  locationJobBoardPath,
  locationJobsPath,
  locationRequestsPath,
  locationBaysPath,
  locationServicesPath,
  locationSettingsLocationPath,
  locationSettingsLocationSchedulePath,
} from "@/routes";
import type { AuthData, Role } from "@/types";

export type NavigationSection =
  | "home"
  | "schedule"
  | "requests"
  | "jobs"
  | "customers"
  | "inventory"
  | "settings";
export type NavigationItem = { key: string; label: string; href: string };
export type NavigationGroup = { key: string; items: NavigationItem[] };

export function pathOnly(url: string) {
  return url.split(/[?#]/)[0].replace(/\/$/, "") || "/";
}

export function pathMatches(url: string, destination: string) {
  const current = pathOnly(url);
  const target = pathOnly(destination);
  return current === target || current.startsWith(`${target}/`);
}

export function dailyNavigation(auth: AuthData): NavigationItem[] {
  const location = auth.location?.id;
  if (!location) return [];
  return [
    {
      key: "home",
      label: "navigation.dashboard",
      href: locationDashboardPath(location),
    },
    {
      key: "requests",
      label: "requests.title",
      href: locationRequestsPath(location),
    },
    {
      key: "board",
      label: "navigation.job_board",
      href: locationJobBoardPath(location),
    },
    { key: "jobs", label: "jobs.title", href: locationJobsPath(location) },
    {
      key: "schedule",
      label: "navigation.calendar",
      href: locationCalendarPath(location),
    },
    {
      key: "customers",
      label: "customers.title",
      href: locationCustomersPath(location),
    },
    {
      key: "inventory",
      label: "inventory.title",
      href: locationDetailingProductsPath(location),
    },
    {
      key: "services",
      label: "services.title",
      href: locationServicesPath(location),
    },
    ...(auth.user?.role === "admin"
      ? [
          {
            key: "settings",
            label: "navigation.location_settings",
            href: locationSettingsLocationPath(location),
          },
        ]
      : []),
  ];
}

export function navigationSection(
  url: string,
  locationId?: string,
  parentSection?: "jobs" | "requests",
): NavigationSection | null {
  const path = pathOnly(url);
  if (
    parentSection &&
    locationId &&
    pathMatches(path, `/${locationId}/recordings`)
  )
    return parentSection;
  if (path === "/user" || path.startsWith("/user/")) return "settings";
  if (!locationId || !pathMatches(path, `/${locationId}`)) return null;
  const resource = path.slice(`/${locationId}`.length).split("/")[1];
  if (!resource) return "home";
  if (resource === "calendar") return "schedule";
  if (resource === "requests") return "requests";
  if (["jobs", "job_board"].includes(resource)) return "jobs";
  if (["customers", "vehicles"].includes(resource)) return "customers";
  if (resource === "detailing_products") return "inventory";
  if (["settings", "bays", "services", "service_offerings"].includes(resource))
    return "settings";
  return null;
}

export function locationSwitchPath(
  url: string,
  nextLocation: string,
  role: Role,
  parentSection?: "jobs" | "requests",
) {
  const path = pathOnly(url);
  const segments = path.split("/");
  const resource =
    segments[2] === "recordings" && parentSection ? parentSection : segments[2];
  const routes: Record<string, (id: string) => string> = {
    jobs: locationJobsPath,
    job_board: locationJobBoardPath,
    calendar: locationCalendarPath,
    requests: locationRequestsPath,
    customers: locationCustomersPath,
    vehicles: locationCustomersPath,
    detailing_products: locationDetailingProductsPath,
    services: locationServicesPath,
    service_offerings: locationServicesPath,
    ...(role === "admin" ? { bays: locationBaysPath } : {}),
  };
  let destination = locationDashboardPath(nextLocation);
  if (segments[1] !== "user" && routes[resource])
    destination = routes[resource](nextLocation);
  if (resource === "settings" && role === "admin") {
    destination =
      segments[3] === "location_schedule"
        ? locationSettingsLocationSchedulePath(nextLocation)
        : locationSettingsLocationPath(nextLocation);
  }
  const query = new URLSearchParams(url.split("?")[1]?.split("#")[0]);
  if (
    resource === "settings" &&
    role === "admin" &&
    segments[3] === "location" &&
    ["regional", "booking"].includes(query.get("section") || "")
  ) {
    return `${destination}?section=${query.get("section")}`;
  }
  const retained = new URLSearchParams();
  const filters: Record<string, string[]> = {
    jobs: ["q", "advance_overdue", "stage"],
    requests: ["q", "state"],
    customers: ["q"],
    detailing_products: ["low_stock"],
    calendar: ["date", "view"],
  };
  for (const key of filters[resource] || []) {
    if (query.has(key)) retained.set(key, query.get(key)!);
  }
  return `${destination}${retained.size ? `?${retained}` : ""}`;
}
export function isCollectionPath(url: string) {
  return /^\/[^/]+(?:\/(jobs|job_board|requests|customers|detailing_products|calendar))?$/.test(
    pathOnly(url),
  );
}

export function returnToCollection(
  candidate: string | null,
  currentUrl: string,
  fallback: string,
  parentSection?: "jobs" | "requests",
) {
  if (
    !candidate ||
    !candidate.startsWith("/") ||
    candidate.startsWith("//") ||
    candidate.includes("\\") ||
    [...candidate].some((character) => character.charCodeAt(0) < 32) ||
    /\/\.\.?\//.test(candidate)
  )
    return fallback;
  const location = pathOnly(currentUrl).split("/")[1];
  if (!isCollectionPath(candidate) || !pathMatches(candidate, `/${location}`))
    return fallback;
  const source = navigationSection(candidate, location);
  const target = navigationSection(currentUrl, location, parentSection);
  if (
    source !== target &&
    !(target === "jobs" && (source === "schedule" || source === "home"))
  )
    return fallback;
  return candidate;
}
export function settingsGroups(auth: AuthData): NavigationGroup[] {
  const admin = auth.user?.role === "admin";
  return [
    {
      key: "personal",
      items: [
        { key: "profile", label: "common.profile", href: "/user/profile" },
        { key: "password", label: "common.password", href: "/user/password" },
      ],
    },
    ...(admin
      ? [
          {
            key: "company",
            items: [
              {
                key: "account",
                label: "common.account",
                href: "/user/account",
              },
              { key: "team", label: "common.team", href: "/user/team" },
              {
                key: "locations",
                label: "common.locations",
                href: "/user/locations",
              },
              { key: "website", label: "website.title", href: "/user/website" },
              {
                key: "integrations",
                label: "integrations.title",
                href: "/user/integrations",
              },
              {
                key: "subscription",
                label: "navigation.subscription",
                href: "/user/billing",
              },
              {
                key: "resources",
                label: "resources.title",
                href: "/user/resource_selection",
              },
            ],
          },
        ]
      : []),
    {
      key: "history",
      items: [
        {
          key: "retained",
          label: "resources.retained_history",
          href: "/user/location_histories",
        },
        ...(admin
          ? [
              {
                key: "activity",
                label: "common.activity",
                href: "/user/activity",
              },
              {
                key: "deletions",
                label: "customer_deletions.queue",
                href: "/user/customer_deletions",
              },
            ]
          : []),
      ],
    },
  ];
}

export function locationSettingsGroups(auth: AuthData): NavigationGroup[] {
  const location = auth.location?.id;
  if (!location || auth.user?.role !== "admin") return [];
  const details = locationSettingsLocationPath(location);
  return [
    {
      key: "location",
      items: [
        {
          key: "location",
          label: "navigation.location_details",
          href: details,
        },
        {
          key: "regional",
          label: "navigation.regional",
          href: `${details}?section=regional`,
        },
        {
          key: "hours",
          label: "navigation.opening_hours",
          href: locationSettingsLocationSchedulePath(location),
        },
        { key: "bays", label: "bays.title", href: locationBaysPath(location) },
        {
          key: "booking",
          label: "navigation.booking_rules",
          href: `${details}?section=booking`,
        },
      ],
    },
  ];
}

export function isLocationSettings(url: string, locationId?: string) {
  return (
    !!locationId &&
    (pathMatches(url, `/${locationId}/settings`) ||
      pathMatches(url, `/${locationId}/bays`))
  );
}

export function activeSettingsItem(auth: AuthData, url: string) {
  const local = isLocationSettings(url, auth.location?.id);
  const items = (
    local ? locationSettingsGroups(auth) : settingsGroups(auth)
  ).flatMap((group) => group.items);
  const section = new URL(url, "https://garageclerk.invalid").searchParams.get(
    "section",
  );
  return items.find((item) => {
    const target = new URL(item.href, "https://garageclerk.invalid");
    return (
      pathMatches(url, item.href) &&
      target.searchParams.get("section") ===
        (section === "regional" || section === "booking" ? section : null)
    );
  });
}

export function activeDailyItem(
  auth: AuthData,
  url: string,
  parent?: "jobs" | "requests",
) {
  if (pathMatches(url, "/user")) return null;
  const location = auth.location?.id;
  if (location && pathMatches(url, locationJobBoardPath(location)))
    return "board";
  if (
    location &&
    ["services", "service_offerings"].some((resource) =>
      pathMatches(url, `/${location}/${resource}`),
    )
  )
    return "services";
  return navigationSection(url, location, parent);
}
